import { normalizeGoogleEventId } from '../../../lib/utils/googleEventId';
import { createEventData } from './eventData';
import type { ActivitySyncContext, CalendarSyncResult } from './types';

/**
 * Le tre funzioni di `useGoogleCalendar` passate dal chiamante — il modulo
 * resta puro (nessuna dipendenza da hook/supabase) e testabile con fake `vi.fn()`.
 */
export interface GoogleCalendarApi {
  createEvent(calendarId: string, event: any, userId: string): Promise<any>;
  updateEvent(eventId: string, calendarId: string, event: any): Promise<any>;
  deleteEvent(eventId: string, calendarId: string): Promise<any>;
}

/**
 * Fonte: LeadStatusUpdateDrawer.tsx:1254 / :1278 / :1634 — stesso pattern di
 * matching sul messaggio d'errore ripetuto 3 volte, unificato qui.
 */
function isAuthError(error: any): boolean {
  const msg = error?.message || '';
  return msg.includes('403') || msg.includes('Forbidden') || msg.includes('non-2xx status code');
}

/** Fonte: LeadStatusUpdateDrawer.tsx:1605-1608 — Meet link SOLO per videochiamata. */
function extractMeetLink(createdEvent: any, type: string): string | null {
  if (type !== 'videochiamata') return null;
  const entryPoints = createdEvent?.conference_data?.entryPoints;
  if (!Array.isArray(entryPoints)) return null;
  const videoEntry = entryPoints.find((ep: any) => ep?.entryPointType === 'video');
  return videoEntry?.uri || null;
}

/**
 * Sincronizza (create/update/move) l'evento Google Calendar per un'attività, in modo puro:
 * riceve l'`api` (le funzioni di `useGoogleCalendar`) come parametro e non fa mai `throw`.
 *
 * Fonti: insert `LeadStatusUpdateDrawer.tsx:1512-1640` (Meet per videochiamata+google_meet,
 * organizer commerciale), update/move `:1170-1285`.
 *
 * Fix rispetto alla fonte (spec §3):
 * 1. Il move è deciso sui ref PERSISTITI `ctx.existing` (mai su stato React async,
 *    a differenza di `previousAssignedTo`/`previousCalendarId` nella fonte).
 * 2. Move anche su cambio di SOLO calendario: confronta `existing.calendarId` con
 *    `ctx.organizer.calendarId` (il calendario di destinazione effettivo — l'organizer
 *    puo' essere il commerciale, non solo l'assegnatario). Nella fonte il ramo move
 *    scattava SOLO su `assigneeChanged`, quindi un cambio di calendario a parità di
 *    assegnatario (es. riconnessione con calendario diverso) restava silenziosamente
 *    sul vecchio evento — bug.
 * 3. Nuovo assegnatario senza sync attiva (`!settings.google.enabled` — quindi ramo
 *    "skipped") con un evento Google già esistente: l'evento va cancellato e i ref
 *    vanno azzerati (`clearRefs: true`), altrimenti resterebbe un evento orfano sul
 *    calendario del vecchio assegnatario. Assente dalla fonte. Il ramo è ora subordinato
 *    a `ctx.allowStaleCleanup !== false` (vedi `types.ts`): con `false` si esce
 *    `skipped` SENZA delete e SENZA `clearRefs`. Perimetro STRETTO: scatta
 *    SOLO su `!settings.google.enabled` — un semplice opt-out per-attività
 *    (`!ctx.syncOptIn`) o un calendario non configurato (`!settings.google.calendarId`
 *    con `enabled` true) NON toccano un evento esistente, altrimenti deselezionare la
 *    spunta cancellerebbe un evento reale e manderebbe email di cancellazione agli invitati.
 * 4. Il move ricrea l'evento con LO STESSO `eventData` (comprensivo di `conference_data`
 *    se applicabile) usato per la create pura → un nuovo Meet viene generato e il suo
 *    link torna nel risultato. Nella fonte il ramo move non gestiva affatto il Meet.
 *    Il `requestId` del Meet sul move è DIVERSO da quello di una create pura (suffisso
 *    `-mv-<vecchioEventId>`) per evitare che Google riusi il token idempotente della
 *    create originale e non rigeneri un Meet nuovo.
 * 5. Mai `throw`: TUTTA la costruzione dell'evento (che tocca `Date.toISOString()`,
 *    può lanciare `RangeError` con una data invalida) e le chiamate all'API vivono
 *    dentro un unico `try`. `'auth_error'` su 403/Forbidden/risposta non-2xx, `'error'`
 *    sugli altri errori, `'skipped'` se `!ctx.syncOptIn || !settings.google.enabled ||
 *    !settings.google.calendarId` (senza mai chiamare `deleteEvent` con `calendarId` null).
 */
export async function syncGoogleEvent(
  ctx: ActivitySyncContext,
  api: GoogleCalendarApi
): Promise<CalendarSyncResult> {
  const { settings, existing } = ctx;

  // Opt-out per-attività: non tocchiamo mai un evento eventualmente esistente.
  if (!ctx.syncOptIn) {
    return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
  }

  // Fix #3 (perimetro stretto): SOLO l'assegnatario senza sync attiva innesca la
  // pulizia dell'evento residuo — mai per opt-out o calendario non configurato.
  // Gate `allowStaleCleanup` (fix round 1 del redesign): quando la pulizia è la prima
  // metà di un move verso Outlook, il chiamante la autorizza SOLO dopo una scrittura
  // Microsoft andata a buon fine. `undefined` = autorizzata (comportamento storico).
  if (!settings.google.enabled) {
    if (existing?.googleEventId && ctx.allowStaleCleanup === false) {
      // Microsoft non ha (ancora) scritto: meglio un evento Google stantio che nessun
      // evento su nessun calendario. Ref intatti, nessuna chiamata API.
      return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
    }
    if (existing?.googleEventId) {
      if (existing.calendarId) {
        try {
          await api.deleteEvent(existing.googleEventId, existing.calendarId);
        } catch {
          // best-effort: azzeriamo comunque i ref, l'evento verrà ripulito manualmente se la delete fallisce
        }
      }
      return { status: 'skipped', eventId: null, calendarId: null, meetLink: null, clearRefs: true };
    }
    return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
  }

  if (!settings.google.calendarId) {
    // Sync abilitata ma calendario non configurato: skip senza toccare l'evento esistente.
    return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
  }

  try {
    // Fix #2: calendario di destinazione = quello dell'organizer risolto (agente o commerciale),
    // non necessariamente quello dell'assegnatario in settings.google.calendarId.
    //
    // Bug CSE 31/08/2026: in UPDATE il chiamante passa organizer null (useActivitySave,
    // policy storica) e settings sono quelle dell'ASSEGNATARIO. Se l'evento era stato
    // creato sul calendario di un ALTRO organizer (il commerciale invitato), il fallback
    // su settings faceva scattare un finto move a ogni edit: delete dall'agenda del
    // consulente + create sul calendario dell'operatore (per Centro Studi Europa, il
    // calendario di gruppo condiviso da 20+ operatori). Con organizer null e assegnatario
    // INVARIATO l'evento resta dov'è: update in place su existing.calendarId.
    // `existing.assignedTo == null` (righe storiche) è trattato come invariato,
    // conservativo. Il move resta per: organizer esplicito diverso (insert/riassegnazione
    // organizer) e assegnatario davvero cambiato.
    const assigneeUnchanged =
      !existing?.assignedTo || existing.assignedTo === ctx.assigneeId;
    const stayInPlace =
      !ctx.organizer && !!existing?.googleEventId && !!existing?.calendarId && assigneeUnchanged;
    const targetCalendarId = stayInPlace
      ? (existing!.calendarId as string)
      : ctx.organizer?.calendarId || settings.google.calendarId;
    const targetUserId = ctx.organizer?.userId || ctx.assigneeId;

    // CRITICAL: costruzione dell'evento DENTRO il try — createEventData() chiama
    // Date.toISOString() e può lanciare RangeError su una data invalida.
    const eventData: any = createEventData(ctx);
    if (ctx.attendees.length > 0) {
      eventData.attendees = ctx.attendees.map((email) => ({ email }));
    }
    // Fonte :1586-1595 — Meet SOLO per videochiamata + provider google_meet. requestId
    // deterministico (activityId + timestamp di startDateTime, MAI Date.now) per i test.
    if (ctx.type === 'videochiamata' && ctx.meetingProvider === 'google_meet') {
      eventData.conference_data = {
        createRequest: {
          requestId: `meet-${ctx.activityId}-${ctx.startDateTime.getTime()}`,
          conferenceSolutionKey: { type: 'hangoutsMeet' },
        },
      };
    }

    // Fix #1: move deciso sui ref persistiti ctx.existing (mai stato async). Guardia
    // hardening: il move richiede ENTRAMBI googleEventId e calendarId valorizzati —
    // mai passare null a deleteEvent.
    const canMove = !!existing?.googleEventId && !!existing?.calendarId;
    const isMove = canMove && existing!.calendarId !== targetCalendarId;
    const isUpdate = canMove && !isMove;

    if (isUpdate) {
      // Fonte :1249-1259 — stesso calendario: update in place, nessun Meet nuovo.
      await api.updateEvent(existing!.googleEventId as string, existing!.calendarId as string, eventData);
      return {
        status: 'ok',
        eventId: existing!.googleEventId,
        calendarId: existing!.calendarId,
        meetLink: null,
      };
    }

    if (isMove) {
      // Fonte :1226-1243, fix #2/#4: delete dal vecchio calendario + create sul nuovo
      // con lo stesso eventData (Meet compreso). requestId Meet diverso dalla create
      // pura per forzare Google a generare un nuovo link invece di riusare quello vecchio.
      if (eventData.conference_data) {
        eventData.conference_data.createRequest.requestId =
          `meet-${ctx.activityId}-${ctx.startDateTime.getTime()}-mv-${existing!.googleEventId}`;
      }
      await api.deleteEvent(existing!.googleEventId as string, existing!.calendarId as string);
    }

    const created = await api.createEvent(targetCalendarId, eventData, targetUserId);
    return {
      status: 'ok',
      eventId: normalizeGoogleEventId(created?.id),
      calendarId: targetCalendarId,
      meetLink: extractMeetLink(created, ctx.type),
      inviteNotSent: created?.metadata?.invite_not_sent === true,
    };
  } catch (error: any) {
    return {
      status: isAuthError(error) ? 'auth_error' : 'error',
      eventId: null,
      calendarId: null,
      meetLink: null,
      error: error?.message || String(error),
    };
  }
}
